import dayjs from 'dayjs';
import { isNil } from 'lodash';

import { SHORT_DATETIME_FORMAT } from 'constants/date';

import {
  PIPELINE_STATUS_AS_OF_FALLBACK,
  PIPELINE_STATUS_AS_OF_PREFIX,
} from './pipelineStatus.constants';

// Normalizes a backend segment ({ label, count }) into the StatusZone display
// shape consumed by BreakdownStatCard. Counts default to 0 so a partial segment
// never renders NaN.
const mapSegment = ({ label, count } = {}) => ({ label, count: count ?? 0 });

const mapSegments = (segments = []) => segments.filter(segment => !isNil(segment)).map(mapSegment);

const mapSubCompanyBreakdown = ({ subCompanyName, segments } = {}) => ({
  subCompanyName,
  segments: mapSegments(segments),
});

// Composes the freshness caption from the backend's real ISO `asOf` timestamp.
// Falls back to a neutral label when the backend omits it — never a fabricated
// "today 06:00".
export const getPipelineStatusAsOfLabel = asOf => {
  if (isNil(asOf)) {
    return PIPELINE_STATUS_AS_OF_FALLBACK;
  }

  return `${PIPELINE_STATUS_AS_OF_PREFIX} ${dayjs(asOf).format(SHORT_DATETIME_FORMAT)}`;
};

/**
 * Maps the milestoneStatus endpoint response into the homepage display shape.
 * Returns null when the response is missing so the reducer holds its empty state.
 * @param {object|null} data - backend response ({ segments, asOf, breakdownBySubCompany })
 * @returns {object|null} display model, or null when not renderable
 */
export const mapPipelineStatus = data => {
  if (isNil(data)) {
    return null;
  }

  const { segments, asOf, breakdownBySubCompany } = data;

  return {
    segments: mapSegments(segments),
    asOf: asOf ?? null,
    asOfLabel: getPipelineStatusAsOfLabel(asOf),
    breakdownBySubCompany: isNil(breakdownBySubCompany)
      ? null
      : breakdownBySubCompany.map(mapSubCompanyBreakdown),
  };
};
